import * as React from "react";
import * as _ from "lodash";
import { reducerBatchJob, reducerSetIfChanged, reducerUpdater } from "./Helpers";
import { Updater } from "@tanstack/react-table";
export interface TreeElement<ItemType, ChildType> {
    item: ItemType;
    expanded: boolean;
    notExpandable?: boolean;
    loading: boolean;
    children?: TreeElement<ChildType, ChildType>[];
    onClick?: () => void;
    selectable?: boolean;
    userData?: any;
    notToggleable?: boolean;
    selected?: boolean;
}


export interface UseTreeViewState<BaseType, ChildType> {
    loading: boolean;
    items: TreeElement<BaseType, ChildType>[];
    selected: Array<TreeElement<BaseType, ChildType> | TreeElement<ChildType, ChildType>>;
    filter?: string;
}

type UseTreeViewActions<BaseType, ChildType> =
    | { type: "batch", value: UseTreeViewActions<BaseType, ChildType>[] }
    | { type: "set_loading", value: boolean }
    | { type: "set_items", value: Updater<TreeElement<BaseType, ChildType>[]> }
    | { type: "set_selected", value: Updater<Array<TreeElement<BaseType, ChildType> | TreeElement<ChildType, ChildType>>> }
    | { type: "set_filter", value?: string }
    ;

const batch = <BaseType extends object = {}, ChildType extends object = {}>(value: UseTreeViewActions<BaseType, ChildType>[]): UseTreeViewActions<BaseType, ChildType> => ({ type: "batch", value })
const set_loading = <BaseType extends object = {}, ChildType extends object = {}>(value: boolean): UseTreeViewActions<BaseType, ChildType> => ({ type: "set_loading", value })
const set_items = <BaseType extends object = {}, ChildType extends object = {}>(value: Updater<TreeElement<BaseType, ChildType>[]>): UseTreeViewActions<BaseType, ChildType> => ({ type: "set_items", value })
const set_selected = <BaseType extends object = {}, ChildType extends object = {}>(value: Updater<Array<TreeElement<BaseType, ChildType> | TreeElement<ChildType, ChildType>>>): UseTreeViewActions<BaseType, ChildType> => ({ type: "set_selected", value })
const set_filter = <BaseType extends object = {}, ChildType extends object = {}>(value?: string): UseTreeViewActions<BaseType, ChildType> => ({ type: "set_filter", value })

const initState = <BaseType extends object = {}, ChildType extends object = {}>(): UseTreeViewState<BaseType, ChildType> => ({
    loading: false,
    items: [],
    selected: [],
});

const reducer = <BaseType extends object = {}, ChildType extends object = {}>(state: UseTreeViewState<BaseType, ChildType>, action: UseTreeViewActions<BaseType, ChildType>): UseTreeViewState<BaseType, ChildType> => {
    switch (action.type) {
        case "batch":
            return reducerBatchJob(state, action.value, reducer);
        case "set_loading":
            return reducerSetIfChanged(state, "loading", action.value);
        case "set_items":
            return reducerUpdater(state, "items", action.value);
        case "set_selected":
            return reducerUpdater(state, "selected", action.value);
        case "set_filter":
            return reducerSetIfChanged(state, "filter", action.value);
    }
}

interface useTreeViewProps<BaseType, ChildType> {
    isBaseItemEqual?: (item1: BaseType, item2: BaseType) => boolean;
    isItemEqual?: (item1: ChildType, item2: ChildType) => boolean;

    getBaseItems: () => Promise<TreeElement<BaseType, ChildType>[] | undefined>;
    getChildrenFromBase: (item: BaseType, currentChildren?: TreeElement<ChildType, ChildType>[]) => Promise<TreeElement<ChildType, ChildType>[] | undefined>;
    getChildrenFromChild: (item: ChildType, currentChildren?: TreeElement<ChildType, ChildType>[]) => Promise<TreeElement<ChildType, ChildType>[] | undefined>;

    spacer?: JSX.Element;
    expander?: (expanded: boolean) => JSX.Element;
    expanderPlaceholder?: JSX.Element;
    itemLoading?: (loading: boolean) => JSX.Element;
    renderBaseItem?: (item: TreeElement<BaseType, ChildType>, selected: boolean, toggleSelect: () => void) => JSX.Element;
    renderItem?: (item: TreeElement<ChildType, ChildType>, selected: boolean, toggleSelect: () => void) => JSX.Element;

    multipleSelect?: boolean;
    filterBaseItem?: (item: BaseType, filter: string) => boolean;
    filterItem?: (item: ChildType, filter: string) => boolean;

    hiddenBaseItems?: BaseType[];
    hiddenItems?: ChildType[];
    selectedItems?: Array<BaseType | ChildType>;
}

const findItemRec = <ChildType extends object = {}>(children: TreeElement<ChildType, ChildType>[] | undefined, item: ChildType, isEq: (item1: ChildType, item2: ChildType) => boolean): TreeElement<ChildType, ChildType> | undefined => {
    //console.log("useTreeView.tsx::37 => children", children);

    if (!children)
        return undefined;
    let f = _.find(children, c => isEq(c.item, item));
    for (let i = 0; i < children.length; i++) {
        if (f)
            break;
        f = findItemRec(children[i].children, item, isEq);

    }
    return f;
}

const findItem = <BaseType extends object = {}, ChildType extends object = {}>(baseItems: TreeElement<BaseType, ChildType>[], item: ChildType, isEq: (item1: ChildType, item2: ChildType) => boolean) => {
    let f: TreeElement<ChildType, ChildType> | undefined;
    for (let i = 0; i < baseItems.length; i++) {
        if (f)
            break;
        //console.log("useTreeView.tsx::56 => baseItems[i]", baseItems[i]);

        f = findItemRec(baseItems[i].children, item, isEq);
    }
    return f;
}
export const MapToTreeElements = <BaseType extends object = {}, ChildType extends object = {}>(data?: BaseType[], toSet?: Partial<TreeElement<BaseType, ChildType>>): TreeElement<BaseType, ChildType>[] =>
    _.map(data, d => ({
        item: d,
        expanded: false,
        loading: false,
        ...toSet
    }));

export const CreateTreeElement = <BaseType extends object = {}, ChildType extends object = {}>(data: BaseType, toSet?: Partial<TreeElement<BaseType, ChildType>>): TreeElement<BaseType, ChildType> =>
({
    item: data,
    expanded: false,
    loading: false,
    ...toSet
});

const useTreeView = <BaseType extends object = {}, ChildType extends object = {}>(p: useTreeViewProps<BaseType, ChildType>) => {
    const {
        getBaseItems, getChildrenFromBase, getChildrenFromChild,
        isBaseItemEqual, isItemEqual,
        spacer, renderBaseItem, renderItem, expander, expanderPlaceholder, itemLoading,
        multipleSelect,
        filterBaseItem, filterItem,
        hiddenBaseItems, hiddenItems,
        selectedItems: selFromOutSide,
    } = p;

    //const {t, currentLanguage, currentNamespace, changeLanguage, createT, imgI18NUpdateCounter } = useImgI18N("general");
    //const {} = useSelector(mapper, shallowCompare);
    const r = React.useMemo(() => (state: UseTreeViewState<BaseType, ChildType>, action: UseTreeViewActions<BaseType, ChildType>) => reducer(state, action), []);
    const [state, dp] = React.useReducer(r, initState<BaseType, ChildType>());
    const { loading, items, selected, filter } = state;


    React.useEffect(() => {
        const l = async () => {
            const data = await getBaseItems();
            dp(batch([
                set_items(data ?? []),
                set_loading(false)
            ]))
        }
        dp(batch([
            set_loading(true),
            set_items([])
        ]))
        l();
    }, [getBaseItems])



    const { isBEq, isIEq } = React.useMemo(() => {
        const isIdEq = (i1: any, i2: any) => i1?.id === i2?.id;
        return {
            isBEq: isBaseItemEqual ?? isIdEq,
            isIEq: isItemEqual ?? isIdEq
        }
    }, [isBaseItemEqual, isItemEqual]);

    React.useEffect(() => {
        if (selFromOutSide) {
            const sel: Array<TreeElement<BaseType, ChildType> | TreeElement<ChildType, ChildType>> = [];
            _.forEach(selFromOutSide, s => {
                const i = _.find(items, i => isBEq(i.item, s as BaseType));
                if (i)
                    sel.push(i);
                const c = findItem(items, s as ChildType, isIEq);
                if (c)
                    sel.push(c);
            })
            dp(set_selected(s => sel));
        }
    }, [selFromOutSide, items, isIEq, isBEq]);

    const { toggleBaseItem, toggleItem } = React.useMemo(() => {

        const toggleBaseItem = async (item: BaseType) => {
            //console.log("useTreeView.tsx::187 => item", item);

            dp(set_items(bi => {
                const cl = _.cloneDeep(bi);
                const idx = _.findIndex(cl, i => isBEq(i.item, item));
                //console.log("useTreeView.tsx::88 => idx", idx);

                if (idx >= 0) {
                    if (cl[idx].expanded) {
                        // it's open just close it
                        cl[idx].expanded = false;
                        return cl;
                    }
                    else {
                        // its not open 
                        // load data async!
                        getChildrenFromBase(cl[idx].item, cl[idx].children).then(children => {
                            dp(set_items(b => {
                                const c = _.cloneDeep(b);
                                const nIdx = _.findIndex(c, i => isBEq(i.item, item));
                                if (nIdx >= 0) {
                                    c[nIdx].children = _.map(children, c => ({ ...c, loading: false })) ?? [];
                                    c[nIdx].loading = false;
                                    return c;
                                }
                                return b;
                            }))
                        });
                        cl[idx].loading = true;
                        cl[idx].expanded = true;
                        return cl;

                    }
                }
                return bi;
            }));
        }
        const toggleItem = async (item: ChildType) => {
            //console.log("useTreeView.tsx::123 => toggleItem", item);
            //console.log("useTreeView.tsx::226 => item", item);

            dp(set_items(bi => {
                const cl = _.cloneDeep(bi);
                const it = findItem(cl, item, isIEq);
                //console.log("useTreeView.tsx::128 => it", it);

                if (it) {
                    if (it.expanded) {
                        it.expanded = false;
                        return cl;
                    }
                    else {
                        it.loading = true;
                        it.expanded = true;
                        getChildrenFromChild(it.item, it.children).then(children => {
                            dp(set_items(b => {
                                const c = _.cloneDeep(b);
                                const i = findItem(c, item, isIEq);
                                if (i) {
                                    i.children = children;
                                    i.loading = false;
                                    return c;
                                }
                                return b;
                            }))
                        });
                        return cl;
                    }
                }
                return bi;
            }))

        }
        return {
            toggleBaseItem,
            toggleItem,
        }
    }, [isBEq, isIEq, getChildrenFromBase, getChildrenFromChild]);


    const TreeItems = React.useMemo(() => () => {

        if (!spacer || !renderBaseItem || !renderItem || !expander || !itemLoading)
            return <div>Treeview is missing renderers!</div>;

        const renderChildren = (i: TreeElement<ChildType, ChildType>, depth: number) => {
            if (_.some(hiddenItems, it => isIEq(it, i.item)))
                return <div />;
            const sel = _.findIndex(selected, s => isIEq((s as any as TreeElement<ChildType, ChildType>).item, i.item));
            // console.log("useTreeView.tsx::182 => selected", selected);

            // console.log("useTreeView.tsx::182 => sel", sel);

            const select = () => {
                if (sel < 0) {
                    if (multipleSelect)
                        dp(set_selected(s => [...s, i]));
                    else
                        dp(set_selected(s => [i]));
                }
                else
                    if (multipleSelect)
                        dp(set_selected(s => {
                            const c = _.clone(s);
                            c.splice(sel, 1);
                            return c;
                        }))
                    else
                        dp(set_selected(s => []));

            }

            const click = () => {
                if (i.onClick)
                    i.onClick();
                if (!i.notToggleable)
                    toggleItem(i.item);
                if (i.selectable)
                    select();
            }

            const show = filter === "" || filter === undefined || filterItem === undefined || filterItem(i.item, filter);
            return (
                <div key={(i.item as any)?.id}>

                    <div style={{ display: "grid", gridTemplateColumns: `${_.join(_.times(depth, x => "auto"), " ")} auto 1fr`, opacity: show ? 1.0 : 0.25 }} onClick={click}>
                        {_.times(depth, x => <React.Fragment key={x}>{spacer}</React.Fragment>)}
                        {i.notExpandable || (i.children ?? []).length === 0 ? <div style={{ opacity: expanderPlaceholder ? 1 : 0 }}>{expanderPlaceholder ? expanderPlaceholder : expander(i.expanded)}</div> : expander(i.expanded)}
                        {renderItem(i, sel >= 0, select)}
                    </div>

                    {i.expanded &&
                        <React.Fragment>
                            {i.loading && itemLoading(i.loading)}
                            {_.map(i.children, c => renderChildren(c, depth + 1))}
                        </React.Fragment>
                    }
                </div>
            );
        }

        return (
            <div>
                {_.map(items, (i, j) => {
                    if (_.some(hiddenBaseItems, it => isBEq(it, i.item)))
                        return <div />;

                    const sel = _.findIndex(selected, s => isBEq((s as any as TreeElement<BaseType, ChildType>).item, i.item));
                    const select = () => {
                        if (sel < 0) {
                            if (multipleSelect)
                                dp(set_selected(s => [...s, i]));
                            else
                                dp(set_selected(s => [i]));
                        }
                        else
                            if (multipleSelect)
                                dp(set_selected(s => {
                                    const c = _.clone(s);
                                    c.splice(sel, 1);
                                    return c;
                                }))
                            else
                                dp(set_selected(s => []));

                    }
                    const click = () => {
                        if (i.onClick)
                            i.onClick();
                        if (!i.notToggleable)
                            toggleBaseItem(i.item);
                        if (i.selectable)
                            select();
                    }
                    const show = filter === "" || filter === undefined || filterBaseItem === undefined || filterBaseItem(i.item, filter);
                    return (
                        <div key={j}>
                            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", opacity: show ? 1.0 : 0.25 }} onClick={click}>
                                {i.notExpandable ? <div /> : expander(i.expanded)}
                                {renderBaseItem(i, sel >= 0, select)}
                            </div>
                            {i.expanded &&
                                <React.Fragment>
                                    {i.loading && itemLoading(i.loading)}
                                    {_.map(i.children, c => renderChildren(c, 1))}
                                </React.Fragment>
                            }
                        </div>
                    );
                })}
            </div>
        );
    }, [items, selected,
        expander, isBEq, isIEq, itemLoading,
        multipleSelect, renderBaseItem, renderItem,
        spacer, toggleBaseItem, toggleItem, filter,
        filterBaseItem, filterItem,
        expanderPlaceholder,
        hiddenBaseItems, hiddenItems])

    const selectedItems = React.useMemo(() => _.map(selected, s => s.item), [selected]);

    const toggleSelectItem = React.useMemo(() => (item: ChildType) => {
        const found = findItem(items, item, isIEq) ?? { item, children: [], expanded: false, loading: false };
        if (found) {
            dp(set_selected(s => {
                const cl = _.clone(s);
                const idx = _.findIndex(cl, c => isIEq(c.item as any as ChildType, item));
                if (idx >= 0) {
                    cl.splice(idx, 1);
                    return cl;
                } else {
                    if (multipleSelect)
                        return [...cl, found];
                    else
                        return [found];
                }
            }))
        }
    }, [items, isIEq, multipleSelect]);
    const toggleSelectBaseItem = React.useMemo(() => (item: BaseType) => {
        const found = _.find(items, ii => isBEq(ii.item, item));
        if (found) {
            dp(set_selected(s => {
                const cl = _.clone(s);
                const idx = _.findIndex(cl, c => isBEq(c.item as any as BaseType, item));
                if (idx >= 0) {
                    cl.splice(idx, 1);
                    return cl;
                } else {
                    if (multipleSelect)
                        return [...cl, found];
                    else
                        return [found];
                }
            }))
        }
    }, [items, isBEq, multipleSelect]);

    const clear = React.useMemo(() => {
        return () => {
            dp(set_selected(s => []));
        }
    }, [dp]);

    const sF = React.useMemo(() => (f?: string) => dp(set_filter(f)), [dp]);
    return {
        loading,
        toggleBaseItem,
        toggleItem,
        items,
        TreeItems,
        selectedItems,
        toggleSelectItem,
        toggleSelectBaseItem,
        setFilter: sF,
        filter,
        clear
    }
}

export type useTreeViewReturn = ReturnType<typeof useTreeView>;

export default useTreeView;